import { ChecklistModuleDefinition } from '../../types/due-diligence';

export const neighborsModule: ChecklistModuleDefinition = {
  code: 'neighbors',
  name: 'Neighbors & Community',
  description: 'Adjacent properties, community, nuisances',
  categories: [
    {
      code: 'adjacent_properties',
      name: 'Adjacent Properties',
      items: [
        {
          code: 'neighbor_inventory',
          title: 'Adjacent property inventory',
          description: 'Who owns neighboring properties',
          isGroupTask: false,
          dataFields: [
            { key: 'neighbor_count', label: 'Number of Adjacent Owners', type: 'number' },
            { key: 'neighbor_types', label: 'Neighbor Types', type: 'array' },
            { key: 'notable_neighbors', label: 'Notable Neighbors', type: 'string' },
          ],
        },
        {
          code: 'neighbor_uses',
          title: 'Neighboring property uses',
          description: 'How neighbors use their land',
          isGroupTask: false,
          dataFields: [
            { key: 'uses', label: 'Adjacent Uses', type: 'multiselect', options: [
              { value: 'residential', label: 'Residential' },
              { value: 'agricultural', label: 'Agricultural' },
              { value: 'timber', label: 'Timber' },
              { value: 'hunting_club', label: 'Hunting Club' },
              { value: 'commercial', label: 'Commercial' },
              { value: 'industrial', label: 'Industrial' },
              { value: 'public', label: 'Public Land' },
              { value: 'vacant', label: 'Vacant' },
            ]},
            { key: 'compatible_uses', label: 'Uses Compatible with Plans', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Incompatible adjacent use' },
            { flag: 'red', condition: 'Industrial or nuisance use adjacent' },
            { flag: 'big_green', condition: 'Adjacent to public/conservation land' },
          ],
        },
        {
          code: 'large_landowners',
          title: 'Large adjacent landowners',
          description: 'Neighboring large tracts',
          isGroupTask: false,
          dataFields: [
            { key: 'large_tracts_adjacent', label: 'Large Tracts Adjacent', type: 'boolean' },
            { key: 'largest_neighbor_acres', label: 'Largest Neighbor Acres', type: 'number', unit: 'acres' },
            { key: 'timber_company', label: 'Timber Company Adjacent', type: 'boolean' },
            { key: 'hunting_club', label: 'Hunting Club Adjacent', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Large undeveloped tracts adjacent' },
          ],
        },
      ],
    },
    {
      code: 'neighbor_relations',
      name: 'Neighbor Relations',
      items: [
        {
          code: 'meet_neighbors',
          title: 'Meet adjacent neighbors',
          description: 'Talk to neighboring property owners',
          isGroupTask: false,
          dataFields: [
            { key: 'neighbors_met', label: 'Neighbors Met', type: 'number' },
            { key: 'reception', label: 'Reception', type: 'select', options: [
              { value: 'very_friendly', label: 'Very Friendly' },
              { value: 'friendly', label: 'Friendly' },
              { value: 'neutral', label: 'Neutral' },
              { value: 'unfriendly', label: 'Unfriendly' },
              { value: 'hostile', label: 'Hostile' },
            ]},
            { key: 'concerns_mentioned', label: 'Concerns Mentioned', type: 'array' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Welcoming neighbors' },
            { flag: 'red', condition: 'Hostile neighbors' },
          ],
        },
        {
          code: 'disputes_history',
          title: 'Dispute history',
          description: 'Historical neighbor disputes',
          isGroupTask: false,
          dataFields: [
            { key: 'disputes_known', label: 'Disputes Known', type: 'boolean' },
            { key: 'dispute_types', label: 'Dispute Types', type: 'array' },
            { key: 'current_conflicts', label: 'Current Conflicts', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Active neighbor disputes' },
            { flag: 'red', condition: 'History of neighbor conflicts' },
          ],
        },
        {
          code: 'boundary_agreements',
          title: 'Boundary agreements',
          description: 'Existing agreements with neighbors',
          isGroupTask: false,
          dataFields: [
            { key: 'agreements_exist', label: 'Agreements Exist', type: 'boolean' },
            { key: 'agreement_types', label: 'Agreement Types', type: 'array' },
            { key: 'fence_agreements', label: 'Fence Agreements', type: 'string' },
          ],
        },
      ],
    },
    {
      code: 'nuisances',
      name: 'Nuisances & Concerns',
      items: [
        {
          code: 'noise_nuisances',
          title: 'Noise assessment',
          description: 'Noise from nearby sources',
          isGroupTask: false,
          dataFields: [
            { key: 'noise_sources', label: 'Noise Sources', type: 'multiselect', options: [
              { value: 'traffic', label: 'Road Traffic' },
              { value: 'train', label: 'Train' },
              { value: 'airplane', label: 'Airplane' },
              { value: 'industrial', label: 'Industrial' },
              { value: 'farm_equipment', label: 'Farm Equipment' },
              { value: 'shooting', label: 'Shooting/Hunting' },
              { value: 'events', label: 'Events/Concerts' },
            ]},
            { key: 'noise_level', label: 'Noise Level', type: 'select', options: [
              { value: 'minimal', label: 'Minimal' },
              { value: 'occasional', label: 'Occasional' },
              { value: 'frequent', label: 'Frequent' },
              { value: 'constant', label: 'Constant' },
            ]},
            { key: 'acceptable', label: 'Noise Acceptable', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Severe noise nuisance' },
            { flag: 'red', condition: 'Significant noise concerns' },
            { flag: 'yellow', condition: 'Some noise to consider' },
          ],
        },
        {
          code: 'odor_nuisances',
          title: 'Odor assessment',
          description: 'Odors from nearby sources',
          isGroupTask: false,
          dataFields: [
            { key: 'odor_sources', label: 'Odor Sources', type: 'array' },
            { key: 'odor_frequency', label: 'Frequency', type: 'string' },
            { key: 'odor_severity', label: 'Severity', type: 'select', options: [
              { value: 'none', label: 'None' },
              { value: 'occasional', label: 'Occasional/Mild' },
              { value: 'moderate', label: 'Moderate' },
              { value: 'severe', label: 'Severe' },
            ]},
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Severe odor nuisance' },
            { flag: 'red', condition: 'CAFO or similar nearby' },
          ],
        },
        {
          code: 'visual_nuisances',
          title: 'Visual assessment',
          description: 'Visual concerns and views',
          isGroupTask: false,
          dataFields: [
            { key: 'visual_concerns', label: 'Visual Concerns', type: 'array' },
            { key: 'views', label: 'Views', type: 'select', options: [
              { value: 'excellent', label: 'Excellent Views' },
              { value: 'good', label: 'Good Views' },
              { value: 'average', label: 'Average' },
              { value: 'poor', label: 'Poor/Obstructed' },
            ]},
            { key: 'privacy', label: 'Privacy Level', type: 'select', options: [
              { value: 'excellent', label: 'Excellent - Very Private' },
              { value: 'good', label: 'Good Privacy' },
              { value: 'moderate', label: 'Moderate Privacy' },
              { value: 'limited', label: 'Limited Privacy' },
            ]},
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Excellent views and privacy' },
            { flag: 'yellow', condition: 'Privacy concerns' },
          ],
        },
        {
          code: 'other_nuisances',
          title: 'Other nuisances',
          description: 'Additional concerns',
          isGroupTask: false,
          dataFields: [
            { key: 'light_pollution', label: 'Light Pollution', type: 'boolean' },
            { key: 'trespassing_issues', label: 'Trespassing Issues', type: 'boolean' },
            { key: 'dumping_issues', label: 'Dumping Issues', type: 'boolean' },
            { key: 'other_concerns', label: 'Other Concerns', type: 'array' },
          ],
          suggestedFlags: [
            { flag: 'yellow', condition: 'Trespassing or dumping issues' },
          ],
        },
      ],
    },
    {
      code: 'community',
      name: 'Community Assessment',
      items: [
        {
          code: 'community_character',
          title: 'Community character',
          description: 'Overall community assessment',
          isGroupTask: false,
          dataFields: [
            { key: 'community_type', label: 'Community Type', type: 'select', options: [
              { value: 'rural', label: 'Rural/Agricultural' },
              { value: 'exurban', label: 'Exurban/Rural Residential' },
              { value: 'small_town', label: 'Small Town' },
              { value: 'suburban', label: 'Suburban' },
            ]},
            { key: 'growth_trend', label: 'Growth Trend', type: 'select', options: [
              { value: 'rapidly_growing', label: 'Rapidly Growing' },
              { value: 'moderate_growth', label: 'Moderate Growth' },
              { value: 'stable', label: 'Stable' },
              { value: 'declining', label: 'Declining' },
            ]},
            { key: 'character_notes', label: 'Character Notes', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Stable rural community' },
            { flag: 'yellow', condition: 'Rapidly changing area' },
          ],
        },
        {
          code: 'future_development',
          title: 'Future development potential',
          description: 'What might be built nearby',
          isGroupTask: false,
          dataFields: [
            { key: 'development_pressure', label: 'Development Pressure', type: 'select', options: [
              { value: 'high', label: 'High' },
              { value: 'moderate', label: 'Moderate' },
              { value: 'low', label: 'Low' },
              { value: 'none', label: 'None' },
            ]},
            { key: 'planned_developments', label: 'Known Planned Developments', type: 'array' },
            { key: 'zoning_changes', label: 'Potential Zoning Changes', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Protected from development' },
            { flag: 'yellow', condition: 'Development pressure exists' },
            { flag: 'red', condition: 'Major development planned nearby' },
          ],
        },
      ],
    },
  ],
};

